"use client"

import * as React from "react"
import { useEffect } from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

type DateRange = {
  from: Date | undefined
  to?: Date | undefined
}

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface DateTimeRangePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: DateRange
  onValueChange?: (range: DateRange) => void
}

function getStartOfDay(d: Date): Date {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}

function getEndOfDay(d: Date): Date {
  const out = new Date(d)
  out.setHours(23, 59, 59, 999)
  return out
}

function getStartOfMonth(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), 1)
  out.setHours(0, 0, 0, 0)
  return out
}

function getEndOfMonth(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  out.setHours(23, 59, 59, 999)
  return out
}

const PRESETS = [
  {
    label: "Today",
    getValue: () => {
      const today = new Date()
      return { from: getStartOfDay(today), to: getEndOfDay(today) }
    },
  },
  {
    label: "Yesterday",
    getValue: () => {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      return { from: getStartOfDay(yesterday), to: getEndOfDay(yesterday) }
    },
  },
  {
    label: "Last 7 days",
    getValue: () => {
      const today = new Date()
      const from = new Date(today)
      from.setDate(today.getDate() - 6)
      return { from: getStartOfDay(from), to: getEndOfDay(today) }
    },
  },
  {
    label: "This month",
    getValue: () => {
      const today = new Date()
      return { from: getStartOfMonth(today), to: getEndOfMonth(today) }
    },
  },
  {
    label: "Last month",
    getValue: () => {
      const today = new Date()
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1)
      return { from: getStartOfMonth(lastMonth), to: getEndOfMonth(lastMonth) }
    },
  },
  {
    label: "Last 3 months",
    getValue: () => {
      const today = new Date()
      const from = new Date(today.getFullYear(), today.getMonth() - 2, 1)
      return { from: getStartOfDay(from), to: getEndOfDay(today) }
    },
  },
] as const

export function DateTimeRangePicker({
  className,
  value,
  onValueChange,
  ...props
}: DateTimeRangePickerProps) {
  const [dateRange, setDateRange] = React.useState<DateRange>({
    from: value?.from ?? undefined,
    to: value?.to ?? undefined,
  })
  const [fromTime, setFromTime] = React.useState("00:00")
  const [toTime, setToTime] = React.useState("23:59")

  useEffect(() => {
    setDateRange({
      from: value?.from ?? undefined,
      to: value?.to ?? undefined,
    })
    if (value?.from) setFromTime(format(value.from, "HH:mm"))
    if (value?.to) setToTime(format(value.to, "HH:mm"))
  }, [value?.from, value?.to])

  const applyTimeToRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined, fromT: string, toT: string): DateRange => {
      if (!from) return { from: undefined, to: undefined }
      const [fromH, fromM] = fromT.split(":").map(Number)
      const [toH, toM] = toT.split(":").map(Number)
      const fromDate = new Date(from)
      fromDate.setHours(isNaN(fromH) ? 0 : fromH, isNaN(fromM) ? 0 : fromM, 0, 0)
      if (!to) return { from: fromDate, to: undefined }
      const toDate = new Date(to)
      toDate.setHours(isNaN(toH) ? 23 : toH, isNaN(toM) ? 59 : toM, 59, 999)
      return { from: fromDate, to: toDate }
    },
    []
  )

  const notifyChange = React.useCallback(
    (range: DateRange) => {
      setDateRange(range)
      if (range.from) setFromTime(format(range.from, "HH:mm"))
      if (range.to) setToTime(format(range.to, "HH:mm"))
      onValueChange?.(range)
    },
    [onValueChange]
  )

  const setSameDaySelection = (day: Date) => {
    const fromDate = getStartOfDay(new Date(day))
    const toDate = getEndOfDay(new Date(day))
    setFromTime("00:00")
    setToTime("23:59")
    notifyChange({ from: fromDate, to: toDate })
  }

  const handleSelectRange = (range: DateRange | undefined) => {
    if (!range) return
    if (range.from && !range.to && dateRange.from && range.from.toDateString() === dateRange.from.toDateString()) {
      setSameDaySelection(range.from)
      return
    }
    const from = range.from ? getStartOfDay(range.from) : undefined
    const to = range.to ? getEndOfDay(range.to) : undefined
    if (from) setFromTime("00:00")
    if (to) setToTime("23:59")
    notifyChange({ from, to })
  }

  const handleFromTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = e.target.value || "00:00"
    setFromTime(t)
    const next = applyTimeToRange(dateRange.from, dateRange.to, t, toTime)
    onValueChange?.(next)
    setDateRange(next)
  }

  const handleToTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = e.target.value || "23:59"
    setToTime(t)
    const next = applyTimeToRange(dateRange.from, dateRange.to, fromTime, t)
    onValueChange?.(next)
    setDateRange(next)
  }

  const formatDateRange = () => {
    if (!dateRange.from) return "Select date range"
    let formatted = format(dateRange.from, "MMM dd, yyyy HH:mm")
    if (dateRange.to) formatted += " – " + format(dateRange.to, "MMM dd, yyyy HH:mm")
    return formatted
  }

  return (
    <div className={cn("grid gap-2", className)} {...props}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              "w-full min-w-[280px] justify-start text-left font-normal",
              !dateRange.from && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="truncate">{formatDateRange()}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3 space-y-3">
            {/* Presets */}
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => notifyChange(preset.getValue())}
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            {/* Calendar */}
            <Calendar
              mode="range"
              defaultMonth={dateRange.from}
              selected={{ from: dateRange.from, to: dateRange.to }}
              onSelect={handleSelectRange}
              numberOfMonths={2}
              className="rounded-md border"
              modifiers={{ interactive: true }}
            />

            {/* Time inputs */}
            {dateRange.from && (
              <div className="grid grid-cols-2 gap-3 border-t pt-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">From (time)</Label>
                  <Input
                    type="time"
                    value={fromTime}
                    onChange={handleFromTimeChange}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">To (time)</Label>
                  <Input
                    type="time"
                    value={toTime}
                    onChange={handleToTimeChange}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            )}

            {/* Same day shortcut */}
            <div className="pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground hover:text-foreground"
                onClick={() => dateRange.from && setSameDaySelection(dateRange.from)}
              >
                Select as same day
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
